import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyEVDCDeviceFormData,
    mapEvdcDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { EVDCDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_VERSION_CONFIG } from "../../../swrConfig"
import { NewEvdcDeviceVersionResponse } from "../../../types/devices/lct/publishEvdcDeviceTypes"
import { START_NEW_LCT_DEVICE_VERSION } from "../../../urls/private"

interface IUseStartNewEVDCDeviceVersionOutput {
    data: EVDCDeviceFormData
    setData: Dispatch<SetStateAction<EVDCDeviceFormData>>
    apiResSystemRef: string | null
    isLoading: boolean
    isValidating: boolean
    error?: AppError
}

const startNewEvdcDeviceVersionMapper = getTsIoMapper(
    NewEvdcDeviceVersionResponse,
    "startNewEvdcDeviceVersionMapper"
)

/**
 * Hook for starting a new device version.
 * Returns data for the new device version, which is then used to populate the device context
 */
const useStartNewEVDCDeviceVersion = (
    deviceRef: string,
    attachmentUUID: string
): IUseStartNewEVDCDeviceVersionOutput => {
    const [data, setData] = useState<EVDCDeviceFormData>(
        getEmptyEVDCDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_VERSION(
            deviceRef,
            attachmentUUID,
            LCTOnlyDatasets.EVDC
        ),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = startNewEvdcDeviceVersionMapper.map(response)
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapEvdcDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError(
                    "Failed to start new EVDC version - data load failed"
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_VERSION_CONFIG
    )

    return {
        data,
        setData,
        apiResSystemRef: apiResSystemRef.current,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewEVDCDeviceVersion
